/* ==========================================================================
   THE PLOT FRAME — where the labels go, and why they are not in the plot.

   Every instrument in this kit draws a trace and, around it, five kinds of
   writing: the channel's name, the range the window actually held, the two
   ends of the y-box, the value under the cursor, and one number per
   threshold rule. For two rounds all five were printed INSIDE the plot with
   a 2.5px halo of the panel ground knocked out behind them, on the theory
   that a halo buys the label its own pixels.

   It does not. A halo keeps a label legible where a trace passes under it;
   it cannot keep the label from claiming a position. A `15%` sitting three
   pixels above the 15% rule and seven below the 40% rule labels neither —
   the operator reads the nearer line, which is the wrong one — and a
   battery pinned near 100% draws its trace straight through its own lane
   name however much ground is knocked out behind the glyphs.

   So the plot gets a box and the writing gets gutters, and the two never
   overlap:

     ┌ padL ──────────┬─ plot ──────────────────────┬──── padR ─┐
     │ ● Battery      │                             │           │
     │ 5.0 – 59.6 %   │  ╲                          │           │
     │        100 %   │ ───╲──────────────────────  ┤─   40 %   │  ← warn rule
     │                │      ╲___                   │           │
     │ ─ ─ ─ ─ ─ ─ ─ ─│ ─ ─ ─ ─ ─╲─ ─ ─ ─ ─ ─ ─ ─ ─ ┤─   15 %   │  ← error rule
     │            0 % │            ╲__________      ┤─    7.9 % │  ← the reading
     └────────────────┴─────────────────────────────┴───────────┘

   EVERY NUMBER IN THE RIGHT GUTTER SITS AT THE HEIGHT IT DESCRIBES — the
   reading included. It is a callout on the trace's own terminal point, with
   a stub at the plot's edge and the same hairline leader the limits get,
   drawn in the trace's colour. A column that mixes positioned numbers with
   one unpositioned one gives the operator no cue which is which, and on a
   faulted battery it draws the reading ABOVE the two limits it has fallen
   through. The reading's stub is 2px where a limit's is a hairline, and the
   reading is the callout that keeps its exact height when the column is
   crowded: it is the robot, the others are constants. See `placeCallouts`.

   Both gutters are FIXED, not derived from the longest string in them —
   that is what makes nine lanes read as a column rather than as nine
   separately-typeset plots, and it is what lets the value and every
   threshold number share one right edge down the whole stack (§4, tabular
   numerals: "the counts form a clean column you can read as a
   distribution"). It is the layout `/fleet/detail`'s telemetry window
   already ships and the layout `auterion-flight-log-review.png` uses beside
   its plots.

   The right gutter is split again, into a NUMBER column and a UNIT column,
   because `79.9 %`, `0.46 m/s` and `81.5 GiB` only align on their digits if
   the unit is allowed its own field (§4: "value and unit are one cell.
   Number in --text-hi, unit in --text-low").
   ========================================================================== */

export interface Frame {
  /** The whole body's width — gutters included. */
  width: number;
  /** Width of the left gutter — name, window range, y-box ends. */
  padL: number;
  /** Width of the right gutter — cursor value, one label per rule. */
  padR: number;
  /** Drawable width between them. */
  plotW: number;
  /** x of the plot's left edge (= padL). */
  x0: number;
  /** x of the plot's right edge. */
  x1: number;
  /** Right-aligned x for a y-axis end or an interior tick value. */
  axisX: number;
  /** Left-aligned x for the channel name and the window range. */
  nameX: number;
  /** Right-aligned x for the NUMBER of any right-gutter readout. */
  valueX: number;
  /** Left-aligned x for the UNIT of any right-gutter readout. */
  unitX: number;
  /** The frame is narrow enough that secondary gutter lines are dropped. */
  tight: boolean;
}

const MAX_L = 146;
const MAX_R = 92;
const MIN_L = 74;
/**
 * THE RIGHT GUTTER HAS FOUR THINGS TO FIT AND 58px ONLY HELD THREE.
 *
 * From the plot's edge outward: a leader corridor, the number, a 3px gap,
 * the unit column. At 58 the number field was 29px, a five-glyph `142.2` at
 * 11px inks 33.3, and the readout was printed hard against the plot's own
 * boundary with its leader elbow running underneath its digits — which is
 * what a four-column comparison grid looked like on a 1600px viewport. 68
 * is the sum of the four: 6 + 33 + 3 + 26. The twelve pixels come out of
 * plot width, which at 190px of trace is a trade worth making once.
 */
const MIN_R = 68;
/** Clear space between the plot's edge and a right-gutter number's first glyph. */
const LEADER_ROOM = 6;

/**
 * The frame for a plot body `width` px wide.
 *
 * The gutters scale with the panel between hard stops, so a 340px card in a
 * comparison grid does not spend two thirds of itself on writing and a
 * 1300px stack does not leave its threshold labels pressed against the
 * panel border. Above `MAX_*` they stop growing: past that the extra width
 * is time resolution, which is the thing the operator actually came for.
 */
export function frameOf(width: number): Frame {
  const padL = Math.round(Math.min(MAX_L, Math.max(MIN_L, width * 0.15)));
  const padR = Math.round(Math.min(MAX_R, Math.max(MIN_R, width * 0.1)));
  const plotW = Math.max(24, width - padL - padR);
  const unitW = padR >= 78 ? 34 : 26;
  return {
    width,
    padL,
    padR,
    plotW,
    x0: padL,
    x1: padL + plotW,
    axisX: padL - 6,
    nameX: 14,
    valueX: width - unitW - 3,
    unitX: width - unitW,
    tight: padL < 118,
  };
}

/** Room a left-gutter caption line has before it would reach the axis column. */
export function captionRoom(f: Frame): number {
  return f.padL - f.nameX - 6;
}

/**
 * The kit's mono advance, in ems. Measured off the rendered stack rather
 * than guessed: `p50` at 10px inks 18.1px and `110.9` inks 30.1px, which is
 * 6.03px a glyph — 0.603em. It is here because two columns of monospace can
 * only be laid out side by side if the layout knows how wide a glyph is;
 * "give it 46px and right-align" is how the stats block came to print `p50`
 * and `110.9` through each other.
 */
export const MONO_ADV = 0.605;

/** Ink width of `chars` monospace glyphs at `px`. */
export function monoWidth(chars: number, px: number): number {
  return chars * px * MONO_ADV;
}

/**
 * Whether a string of `chars` monospace glyphs at `px` fits in `room`.
 * Used to decide whether a secondary caption line is printed at all rather
 * than to ellipsise it — a truncated range is a number the operator cannot
 * use. The 0.62 is MONO_ADV with a glyph of slack, so a line is dropped
 * before it can touch the column to its right.
 */
export function monoFits(chars: number, px: number, room: number): boolean {
  return chars * px * 0.62 <= room;
}

/**
 * THE ONE PITCH RULE, AND IT IS THE SAME NUMBER EVERYWHERE.
 *
 * Ten-pixel type set on a ten-pixel pitch has zero leading, which is not
 * tight typography, it is two numbers touching. Every stack of small labels
 * in this kit — the right gutter's callouts, the y-axis's ends against its
 * interior ticks, the isolate panel's stats rows — is laid out on this, so
 * a lane cannot butt three numbers together in one place while keeping them
 * apart in another. Where the space is genuinely not there, a LABEL is
 * dropped and its mark is kept: an unlabelled rule still says where the
 * limit is, whereas three labels in thirty pixels say nothing at all.
 */
export const MIN_PITCH = 12;

/* --------------------------------------------------------------------------
   THE RIGHT GUTTER IS A COLUMN OF CALLOUTS, AND EVERY ONE OF THEM IS
   POSITIONED.
   -------------------------------------------------------------------------- */

/**
 * Baseline offset that centres a 10px label on the height it describes.
 */
export const LABEL_RISE = 3.5;

/**
 * What a right-gutter readout is about.
 *
 *   `value`  the reading the lane is quoting — the sample under the cursor,
 *            or the newest one when nobody is pointing. Its height is the
 *            trace's own height at that sample.
 *   `warn` / `bad`  a published limit. Its height is its rule's.
 */
export type CalloutKind = 'value' | 'warn' | 'bad';

export interface Callout {
  key: string;
  kind: CalloutKind;
  /** The height this readout is really about — trace point, or rule. */
  anchorY: number;
  /** Kept first when the lane cannot hold every label. Lower wins. */
  rank: number;
}

export interface PlacedCallout extends Callout {
  /** Where the label is drawn, or null when the lane has no room for it. */
  labelY: number | null;
}

/** A published limit, once it has been given an anchor and a rank. */
export interface LimitCallout extends Callout {
  kind: 'warn' | 'bad';
  v: number;
}

/**
 * EVERY NUMBER IN THE RIGHT GUTTER SITS AT THE HEIGHT IT DESCRIBES.
 *
 * For three rounds this function placed the threshold labels and only the
 * threshold labels. The value at the cursor was pinned to the top of the
 * lane, level with the channel's name — so one column carried two kinds of
 * number that looked identical and only one of which meant anything by its
 * position. On `koko-04` that column read
 *
 *     7.9 %      ← the battery, pinned to the top of the lane
 *      40 %      ← the warn rule, at the warn rule's height
 *      15 %      ← the error rule, at the error rule's height
 *
 * with the trace itself well below both rules. A battery that has fallen
 * through both of its limits was drawn ABOVE them, which is the exact
 * opposite of the reading. The same column on an isolated Temp lane put
 * `39.2 °C` 338px above the point it was quoting, and a three-robot Battery
 * card stacked five numbers in one run with two of them positioned and
 * three of them not.
 *
 * So there is no privileged readout any more. The value is a callout, the
 * limits are callouts, they are laid out together against one set of rules,
 * and each is drawn with the same 4px stub at the plot's edge and the same
 * hairline leader when it has to move. A number in this column is now
 * always a claim about a height, and the ink says which height.
 *
 * TWO THINGS DECIDE THE COLUMN, AND RANK DECIDES BOTH.
 *
 * 1. WHO KEEPS THEIR HEIGHT. One callout in a lane is placed at its own
 *    anchor and everything else is arranged around it, and that one is the
 *    lowest rank present — the reading. A displacement shared evenly across
 *    a crowded column is a column in which nothing is where it says it is;
 *    on a three-robot CPU card that produced `90 %` / `75 %` / `30.9 %`
 *    butted in a thirty-pixel band with the live number lowest and no cue
 *    that it was the live one. A limit label carries a hairline leader back
 *    to its rule and can afford to move. The reading is a callout on a
 *    moving trace and cannot: if it is not level with the trace's terminal
 *    point, the operator has to work out which of three numbers is the
 *    robot.
 *
 * 2. WHO SURVIVES A LANE THAT IS TOO SHORT. Capacity is measured at
 *    `MIN_PITCH`, and where there are more callouts than lines the highest
 *    ranks are dropped — the warn label first, then the error label, never
 *    the reading. A dropped label does NOT take its mark with it: the rule
 *    is still drawn across the plot and the callout still stubs the plot's
 *    edge at the height it is about, so a 44px lane says "there is a limit
 *    here, and here is the value" rather than printing three numbers on top
 *    of each other and meaning none of them.
 *
 * Around the pivot the passes are outward and monotone — up from it taking
 * each label's own height or one pitch above its lower neighbour, down from
 * it the mirror — and the two ends are then clamped back inside the lane,
 * which is what keeps a battery's `15 %` when the whole column has been
 * pushed against the floor.
 *
 * `ceiling` and `floor` are the first and last baselines the lane will
 * accept.
 */
export function placeCallouts<T extends Callout>(
  items: readonly T[],
  ceiling: number,
  floor: number,
  lineH = MIN_PITCH,
): (T & { labelY: number | null })[] {
  if (items.length === 0) return [];

  const capacity = Math.max(0, Math.floor((floor - ceiling) / lineH) + 1);
  const dropped = new Set<string>();
  if (items.length > capacity) {
    const byRank = [...items].sort((a, b) => b.rank - a.rank || b.anchorY - a.anchorY);
    for (let i = 0; i < items.length - capacity; i += 1) dropped.add(byRank[i]!.key);
  }

  const keep = items.filter((c) => !dropped.has(c.key)).sort((a, b) => a.anchorY - b.anchorY);
  const n = keep.length;
  const at = new Array<number>(n);

  if (n > 0) {
    /* The one callout that keeps its own height. Ties go to the topmost, so
       two readings in one lane stay in trace order. */
    let pivot = 0;
    for (let i = 1; i < n; i += 1) if (keep[i]!.rank < keep[pivot]!.rank) pivot = i;

    /* Its anchor, but never so close to an edge that the callouts above or
       below it have nowhere left to go. Capacity has already guaranteed the
       window between these two bounds is not empty. */
    at[pivot] = Math.min(
      Math.max(keep[pivot]!.anchorY + LABEL_RISE, ceiling + pivot * lineH),
      floor - (n - 1 - pivot) * lineH,
    );
    for (let i = pivot - 1; i >= 0; i -= 1) {
      at[i] = Math.min(keep[i]!.anchorY + LABEL_RISE, at[i + 1]! - lineH);
    }
    for (let i = pivot + 1; i < n; i += 1) {
      at[i] = Math.max(keep[i]!.anchorY + LABEL_RISE, at[i - 1]! + lineH);
    }
    /* An anchor above the lane's first baseline (or below its last) drags
       the whole run past an edge; push it back and re-space, which spends
       the displacement on the callouts nearest that edge. */
    if (at[0]! < ceiling) {
      at[0] = ceiling;
      for (let i = 1; i < n; i += 1) at[i] = Math.max(at[i]!, at[i - 1]! + lineH);
    }
    if (at[n - 1]! > floor) {
      at[n - 1] = floor;
      for (let i = n - 2; i >= 0; i -= 1) at[i] = Math.min(at[i]!, at[i + 1]! - lineH);
    }
  }

  const placedBy = new Map<string, number>();
  keep.forEach((c, i) => {
    const yy = at[i]!;
    /* Only where the lane genuinely has no room left: the anchor is still
       drawn and still stubbed, and an unreadable number is worse than none. */
    if (yy >= ceiling - 0.5 && yy <= floor + 0.5) placedBy.set(c.key, yy);
  });

  return items.map((c) => ({ ...c, labelY: placedBy.get(c.key) ?? null }));
}

export interface Limit {
  v: number;
  kind: 'warn' | 'bad';
}

/** The limits a channel publishes that fall inside the drawn box, in order. */
export function limitCallouts(
  limits: readonly Limit[],
  y: (v: number) => number,
  box: { lo: number; hi: number },
): LimitCallout[] {
  return limits
    .filter((l) => l.v >= box.lo && l.v <= box.hi)
    .map((l) => ({ ...l, key: l.kind, anchorY: y(l.v), rank: l.kind === 'bad' ? 1 : 2 }));
}

/**
 * A label more than this far from its anchor needs a leader drawn to it. Two
 * limits can be four pixels apart on a 45px lane (CPU's 75 and 90 against a
 * box of 11–94) and no typography puts two legible numbers four pixels
 * apart — so the label moves and a hairline says which height it came from.
 * That is how an engineering drawing handles a crowded callout, and it is
 * the only answer that neither drops the number nor lies about its height.
 */
export const LEADER_MIN = 3;

/**
 * The callout: stub at the anchor, up or down the gutter, out to the label.
 *
 * `endX` is the label's own left edge, so the elbow stops where the digits
 * begin instead of at a fixed offset — a fixed one either runs under a wide
 * number (`142.2` starts 6px into the gutter) or stops 8px short of a narrow
 * one (`40`) and leaves the leader looking like it belongs to nothing.
 */
export function leaderPath(x1: number, anchorY: number, labelY: number, endX: number): string {
  const knee = x1 + LEADER_ROOM - 1;
  return `M${x1},${anchorY} H${knee} V${labelY - LABEL_RISE} H${Math.max(knee, endX)}`;
}
